import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, Lock, Unlock, AlertTriangle, Globe, Trash2, Check, Loader2, Clock, Award, ExternalLink,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmt(n) { return (n || 0).toLocaleString(); }

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / 86400000);
}

export default function UserDashboardFoundersSection({ token, user }) {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWaiverFor, setShowWaiverFor] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/redemption/my-licenses`, { headers });
      setLicenses(res.data.licenses || []);
    } catch (e) {
      console.error('founders fetch failed', e);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-[#A1A1AA]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your Founders licenses…
      </div>
    );
  }

  if (!licenses.length) {
    return (
      <div className="bg-[#111] border border-white/10 rounded-2xl p-8 text-center text-[#A1A1AA]">
        <Award className="w-10 h-10 mx-auto mb-2 opacity-40" />
        <p>You don&apos;t have an LTD redemption license on this account.</p>
        <p className="text-sm mt-2">If you bought one on a deal platform, redeem it at <Link to="/redeem" className="underline text-[#00E5FF]">/redeem</Link>.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {user?.founder_circle_member && (
        <FoundersCircleBanner user={user} />
      )}

      {licenses.map(lic => (
        <LicenseCard
          key={lic.id}
          lic={lic}
          onOpenWaiver={() => setShowWaiverFor(lic)}
          onChange={fetchData}
          token={token}
        />
      ))}

      {showWaiverFor && (
        <WaiverModal
          license={showWaiverFor}
          token={token}
          onClose={() => setShowWaiverFor(null)}
          onSuccess={() => { setShowWaiverFor(null); fetchData(); }}
        />
      )}
    </div>
  );
}

function FoundersCircleBanner({ user }) {
  return (
    <div className="bg-gradient-to-r from-purple-500/20 via-blue-500/20 to-cyan-500/20 border border-purple-500/30 rounded-2xl p-6 flex items-center gap-4">
      <Award className="w-10 h-10 text-purple-300 flex-shrink-0" />
      <div className="flex-1">
        <div className="font-bold text-white">Founder #{user.founder_number} · Founders Circle</div>
        <p className="text-sm text-[#A1A1AA]">You&apos;re in. Thank you for trusting us early.</p>
      </div>
    </div>
  );
}

function LicenseCard({ lic, onOpenWaiver, onChange, token }) {
  const [domainInput, setDomainInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };
  const activeDomains = (lic.activated_domains || []).filter(d => d.status === 'active');
  const slotsLeft = Math.max(0, (lic.max_domains || 0) - activeDomains.length);

  const deactivate = async (domain) => {
    if (!window.confirm(`Deactivate domain "${domain}"? It will free a slot you can use for another site.`)) return;
    setBusy(true); setError(null);
    try {
      await axios.post(`${API_URL}/api/redemption/deactivate-domain`, { license_id: lic.id, domain }, { headers });
      onChange?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const unlockDays = daysUntil(lic.next_unlock_at);

  return (
    <div className="bg-[#111] border border-white/10 rounded-2xl p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5 text-[#00E5FF]" />
            <h3 className="text-lg font-bold text-white">{lic.package_name}</h3>
            <span className="text-xs px-2 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded">Lifetime</span>
            {lic.source_platform && (
              <span className="text-xs px-2 py-0.5 bg-white/5 text-[#A1A1AA] border border-white/10 rounded">via {lic.source_platform}</span>
            )}
          </div>
          <code className="text-xs text-[#A1A1AA] break-all">{lic.license_key}</code>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Sites used" value={`${activeDomains.length}/${lic.max_domains}`} />
        <Stat label="Slots free" value={slotsLeft} accent={slotsLeft === 0 ? 'warn' : null} />
        <Stat label="Daily cap" value={fmt(lic.daily_credit_cap)} />
        <Stat label="Used today" value={fmt(lic.credits_consumed_today)} />
      </div>

      {/* Pending unlock */}
      {lic.pending_unlock_credits > 0 && (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-blue-300 mt-0.5" />
              <div>
                <div className="font-medium text-white">{fmt(lic.pending_unlock_credits)} credits locked</div>
                <div className="text-sm text-[#A1A1AA]">
                  {unlockDays === 0 ? 'Unlocks today' :
                   unlockDays === 1 ? 'Unlocks in 1 day' :
                   unlockDays != null ? `Unlocks in ${unlockDays} days` : 'Unlock date pending'}
                  {lic.next_unlock_at && <> · {new Date(lic.next_unlock_at).toLocaleDateString()}</>}
                </div>
              </div>
            </div>
            <button onClick={onOpenWaiver}
              className="px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 text-amber-200 text-sm rounded inline-flex items-center gap-1.5">
              <Unlock className="w-4 h-4" /> Unlock now (waive refund)
            </button>
          </div>
        </div>
      )}

      {/* Domains */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-white flex items-center gap-1.5">
            <Globe className="w-4 h-4 text-[#A1A1AA]" /> Active sites
          </h4>
          <span className="text-xs text-[#A1A1AA]">{activeDomains.length}/{lic.max_domains}</span>
        </div>
        {error && <div className="px-3 py-2 mb-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-300">{error}</div>}
        {activeDomains.length === 0 ? (
          <p className="text-sm text-[#A1A1AA]">No active sites yet. Install the plugin and your first site will auto-lock here.</p>
        ) : (
          <ul className="space-y-1">
            {activeDomains.map(d => (
              <li key={d.domain} className="flex items-center justify-between bg-black/20 border border-white/5 rounded px-3 py-2">
                <span className="text-sm font-mono text-white">{d.domain}</span>
                <button onClick={() => deactivate(d.domain)} disabled={busy}
                  className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1 disabled:opacity-50">
                  <Trash2 className="w-3.5 h-3.5" /> Deactivate
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const ring = accent === 'warn' ? 'border-amber-500/30 bg-amber-500/5' : 'border-white/5 bg-black/30';
  const txt = accent === 'warn' ? 'text-amber-200' : 'text-white';
  return (
    <div className={`rounded-lg p-3 border ${ring}`}>
      <div className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-lg font-bold ${txt}`}>{value}</div>
    </div>
  );
}

const WAIVER_PHRASE = 'I_UNDERSTAND_THIS_IS_FINAL';

function WaiverModal({ license, token, onClose, onSuccess }) {
  const [agreed, setAgreed] = useState(false);
  const [confirmStep2, setConfirmStep2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      const res = await axios.post(`${API_URL}/api/redemption/waive-refund-window`, {
        license_id: license.id,
        confirmation: WAIVER_PHRASE,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onSuccess?.(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#111] border border-white/10 rounded-2xl max-w-2xl w-full p-6 md:p-8 space-y-5 my-8">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-amber-400" />
          <h3 className="text-xl font-bold text-white">Waive your refund window?</h3>
        </div>

        <p className="text-sm text-[#A1A1AA] leading-relaxed">
          You bought this lifetime license on <strong>{license.source_platform}</strong>. The platform&apos;s standard refund window protects you for a defined period. By waiving it now, you trade that protection for immediate credit unlock.
        </p>

        <div className="bg-black/40 border border-white/10 rounded-lg p-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-[#A1A1AA]">Credits unlocking now</span><strong className="text-white">{fmt(license.pending_unlock_credits)}</strong></div>
          <div className="flex justify-between"><span className="text-[#A1A1AA]">Refund right after waiver</span><span className="text-amber-300 font-semibold">Forfeited (final)</span></div>
          <div className="flex justify-between"><span className="text-[#A1A1AA]">Lifetime access</span><span className="text-emerald-300 font-semibold">Unchanged</span></div>
        </div>

        <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4 text-sm text-amber-200 leading-relaxed space-y-2">
          <p><strong>Legal effect:</strong> By proceeding you waive your right to obtain a refund through {license.source_platform || 'the platform you bought from'} for this purchase. The license remains lifetime and yours forever, but the refund window is closed permanently. We will email you and the platform a confirmation receipt.</p>
          <p>This action is <strong>irreversible</strong>. You cannot undo a waiver once submitted.</p>
        </div>

        <label className="flex items-start gap-2 cursor-pointer text-sm text-white">
          <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
            className="mt-1 w-4 h-4" />
          <span>I understand that I am voluntarily and permanently waiving my refund right under {license.source_platform || 'the platform'}, in exchange for immediate access to {fmt(license.pending_unlock_credits)} credits.</span>
        </label>

        {agreed && !confirmStep2 && (
          <button onClick={() => setConfirmStep2(true)}
            className="w-full px-6 py-2.5 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 text-amber-200 rounded font-medium">
            I understand &mdash; show me the final confirmation
          </button>
        )}

        {confirmStep2 && (
          <div className="space-y-3 border-t border-white/10 pt-5">
            <p className="text-sm text-white">
              Final step. Click below to permanently waive your refund right and unlock your credits.
              You will receive a confirmation email within 60 seconds.
            </p>
            {error && <div className="px-3 py-2 bg-red-500/10 border border-red-500/20 rounded text-sm text-red-300">{error}</div>}
            <div className="flex gap-3">
              <button onClick={onClose} disabled={submitting}
                className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded font-medium">
                Cancel
              </button>
              <button onClick={submit} disabled={submitting}
                className="flex-1 px-6 py-3 bg-amber-500/30 hover:bg-amber-500/40 border border-amber-500/40 text-amber-100 rounded font-bold flex items-center justify-center gap-1.5">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Yes, waive my refund right
              </button>
            </div>
          </div>
        )}

        {!confirmStep2 && (
          <button onClick={onClose} className="w-full px-6 py-2.5 bg-white/5 hover:bg-white/10 text-white rounded text-sm">
            Cancel — keep my refund window
          </button>
        )}
      </div>
    </div>
  );
}
